import { formatMoney, getDecimalFormatter } from "./formatMoney";

/**
 * `value` is in minor units at `scaleFactor` decimals. `includeSymbol: false` returns the plain
 * decimal number. `compact` drops the cents of a whole amount.
 */
// Kept for the callers that print a payment line digit for digit: cents stay unless `compact`.
// A display price goes through `formatMoney`, which drops zero cents.
export function formatCurrency(
  {
    value,
    scaleFactor,
    currency,
    includeSymbol = true,
    compact = false,
  }: {
    value: number;
    scaleFactor: number;
    currency: string;
    includeSymbol?: boolean;
    compact?: boolean;
  },
  locale: string,
): string {
  if (!includeSymbol) {
    return getDecimalFormatter(locale, scaleFactor).format(
      value / 10 ** scaleFactor,
    );
  }
  return formatMoney({ value, scaleFactor, currency }, locale, {
    wholeWhenZeroCents: compact,
  });
}

/**
 * Get the currency symbol for a given currency code and locale.
 */
export function getCurrencySymbol(currency: string, locale: string): string {
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(1);

    const decimalFormatted = new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(1);

    let symbol = formatted.replace(decimalFormatted, "").trim();
    symbol = symbol.replace(/^[\s\u00A0]+|[\s\u00A0]+$/g, "");
    return symbol;
  } catch {
    return currency;
  }
}
